import { useEffect, useState } from "react";
import { Banknote, QrCode, Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { DELIVERY_LABEL } from "@/lib/shipping";

const METHODS = [
  { id: "tunai", label: "Tunai", icon: Banknote },
  { id: "qris", label: "QRIS", icon: QrCode },
  { id: "transfer", label: "Transfer", icon: Landmark },
];

const presets = (total) => {
  const set = new Set([total]);
  [5000, 10000, 20000, 50000, 100000].forEach((n) => set.add(Math.ceil(total / n) * n));
  return [...set].filter((v) => v >= total).sort((a, b) => a - b).slice(0, 5);
};

export const PaymentDialog = ({ open, onOpenChange, cart, total, customer, onSuccess }) => {
  const [method, setMethod] = useState("tunai");
  const [cash, setCash] = useState("");
  const [note, setNote] = useState("");
  const [customerWa, setCustomerWa] = useState("");
  const [delivery, setDelivery] = useState("ambil");
  const [shipping, setShipping] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const d = customer?.delivery;
      setMethod("tunai");
      setCash("");
      setNote(customer?.name ? `Pesanan: ${customer.name}` : "");
      setCustomerWa(customer?.wa || "");
      setDelivery(d?.method || "ambil");
      setShipping(d?.fee != null ? String(d.fee) : "");
      setAddress(d?.address || "");
    }
  }, [open, customer]);

  const fee = delivery === "antar" ? Math.max(0, Number(shipping) || 0) : 0;
  const grand = total + fee;
  const cashNum = Number(cash) || 0;
  const change = cashNum - grand;
  const canPay = method !== "tunai" || cashNum >= grand;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/transactions", {
        items: cart.map((c) => ({ product_id: c.id, qty: c.qty, options: c.options })),
        payment_method: method,
        cash_paid: method === "tunai" ? cashNum : 0,
        note,
        customer_wa: customerWa.trim(),
        delivery_method: delivery,
        shipping_fee: fee,
        delivery_address: delivery === "antar" ? address.trim() : "",
      });
      toast.success("Transaksi berhasil disimpan");
      onSuccess(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="payment-dialog">
        <DialogTitle className="font-display">Pembayaran</DialogTitle>
        <div className="rounded-xl bg-emerald-50 p-4 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-emerald-700">Total Belanja</p>
          <p className="font-mono text-3xl font-bold text-emerald-800" data-testid="payment-total">{formatRupiah(grand)}</p>
          {fee > 0 && <p className="mt-1 text-xs text-emerald-700" data-testid="payment-total-breakdown">Belanja {formatRupiah(total)} + ongkir {formatRupiah(fee)}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(DELIVERY_LABEL).map(([id, label]) => (
            <button key={id} type="button" onClick={() => setDelivery(id)} data-testid={`payment-delivery-${id}`}
              className={`rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${delivery === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>
              {label}
            </button>
          ))}
        </div>
        {delivery === "antar" && (
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <Input type="number" inputMode="numeric" min="0" value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder="Ongkir" className="font-mono" data-testid="payment-shipping-input" />
            <Input maxLength={200} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Alamat pengiriman" data-testid="payment-address-input" />
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          {METHODS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setMethod(id)}
              data-testid={`payment-method-${id}`}
              className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 text-sm font-semibold transition-colors ${
                method === id ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <Icon className="h-5 w-5" /> {label}
            </button>
          ))}
        </div>

        {method === "tunai" ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Uang Diterima</Label>
              <Input type="number" inputMode="numeric" min="0" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="0" className="h-11 font-mono text-lg" data-testid="payment-cash-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              {presets(grand).map((v, i) => (
                <Button key={v} type="button" variant="outline" size="sm" onClick={() => setCash(String(v))} data-testid={`payment-preset-${i}`}>
                  {v === grand ? "Uang Pas" : formatRupiah(v)}
                </Button>
              ))}
            </div>
            <div className={`flex justify-between rounded-xl px-4 py-3 ${change >= 0 && cash ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}>
              <span className="text-sm font-medium">{change < 0 && cash ? "Kurang" : "Kembalian"}</span>
              <span className="font-mono text-lg font-bold" data-testid="payment-change">{formatRupiah(Math.abs(cash ? change : 0))}</span>
            </div>
          </div>
        ) : (
          <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="payment-manual-info">
            Pastikan pembayaran {method === "qris" ? "QRIS" : "transfer"} sudah diterima, lalu simpan transaksi (dicatat manual).
          </p>
        )}

        <div className="space-y-1.5">
          <Label>No. WA Pelanggan (opsional)</Label>
          <Input inputMode="tel" maxLength={20} value={customerWa} onChange={(e) => setCustomerWa(e.target.value.replace(/[^0-9+\- ]/g, ""))} placeholder="08xxxxxxxxxx — untuk kirim struk" data-testid="payment-customer-wa-input" />
        </div>

        <div className="space-y-1.5">
          <Label>Catatan (opsional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. nama pembeli / no. referensi" data-testid="payment-note-input" />
        </div>

        <Button disabled={!canPay || loading} onClick={submit} className="h-12 w-full bg-emerald-600 text-base hover:bg-emerald-700" data-testid="payment-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Transaksi
        </Button>
      </DialogContent>
    </Dialog>
  );
};
